// The Page tab's look picker, drawn like Flowmark's: a 2-column grid of
// tiles, each a small strip painted in that look's own page color with "Aa" in
// its heading font and text color, and a pill in its brand color. The chosen
// tile gets an ink ring. Clicking a tile applies the look (style, font, brand
// and button colors) and keeps the logo and words.
import type { Archetype } from '../model/pages';
import { ARCHETYPE_LIST, type ArchetypeDef } from '../render/archetypes';
import { styleFor } from '../render/styles';
import { FONTS } from '../render/fonts';
import { HINT } from './InspectorKit';

// The heading fonts the tiles use. Loaded with the same stylesheet links the
// page itself uses, so a copy that reaches the canvas frame changes nothing.
const LOOK_FONT_HREFS = [...new Set(ARCHETYPE_LIST.map((a) => FONTS[a.font]?.href).filter((h): h is string => !!h))];

/** The strip's colors and type for one look. */
export function lookPreview(a: ArchetypeDef): { surfaceClass: string; background?: string; fontFamily?: string; fontWeight: number; brand: string } {
  const s = styleFor(a.style);
  const font = FONTS[a.font];
  return {
    // `band` is the page's background and text color as classes; `surface`
    // is the same background as a plain color.
    surfaceClass: s.band,
    background: s.surface,
    fontFamily: font?.display,
    // Single-weight display faces render at their real weight, never a faux bold.
    fontWeight: font?.regular ? 400 : 700,
    brand: a.brand_color,
  };
}

export function LookTiles({ value, onPick }: {
  /** The look applied last (theme.archetype). */
  value: Archetype | undefined;
  onPick: (key: Archetype) => void;
}) {
  return (
    <div className="grid min-w-0 gap-2.5">
      {LOOK_FONT_HREFS.map((href) => <link key={href} rel="stylesheet" href={href} />)}
      <div role="group" aria-label="Looks" className="grid min-w-0 grid-cols-2 gap-2">
        {ARCHETYPE_LIST.map((a) => {
          const on = value === a.key;
          const p = lookPreview(a);
          return (
            <button key={a.key} type="button" aria-pressed={on} title={a.description} data-look={a.key} onClick={() => onPick(a.key)}
              className={`grid min-w-0 gap-1.5 rounded-2xl border bg-surface p-[5px] text-left transition-[border-color,box-shadow,transform] duration-150 ease-[cubic-bezier(.2,0,0,1)] active:scale-[.98] motion-reduce:transition-none ${on ? 'border-text shadow-[0_0_0_1px_var(--text)]' : 'border-border hover:border-borderHi'}`}>
              <span aria-hidden data-look-strip
                className={`flex h-11 min-w-0 items-end justify-between rounded-[5px] px-2 py-1.5 shadow-[inset_0_0_0_1px_var(--pe-hairline-soft)] ${p.surfaceClass}`}
                style={{ background: p.background }}>
                <span className="text-[15px] leading-none" style={{ fontFamily: p.fontFamily, fontWeight: p.fontWeight }}>Aa</span>
                <span className="mb-px h-2 w-[22px] shrink-0 rounded-full" style={{ background: p.brand }} />
              </span>
              <span className={`truncate px-0.5 pb-px text-xs font-medium ${on ? 'text-text' : 'text-text/80'}`}>{a.label}</span>
            </button>
          );
        })}
      </div>
      <p className={HINT}>A look sets colors, type and buttons together. Your logo and words stay the same.</p>
    </div>
  );
}
